import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import React, { useEffect } from 'react'
import { useLoginMutation } from 'store/slices/auth/login-slice'
import { RootState } from '../../store/glimpse-store'
import { setCredentials } from '../../store/slices/auth/auth-slice'
import { displayError } from '../../components/Toast/toast.component'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { ILoginUser } from 'interfaces/user.interface'
import FormikInput from 'components/input/formik-input'
import Button from 'components/button/button'
import { emailRegex } from 'constants/regex'

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address')
    .matches(emailRegex, 'Invalid email Address')
    .required('Email is required..!'),
  password: Yup.string().required('Password is required..!'),
})

const initialValues = { email: '', password: '' }

const LoginScreen: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [login, { isLoading }] = useLoginMutation()
  const { userInfo } = useSelector((state: RootState) => state.auth)

  useEffect(() => {
    if (userInfo) {
      const { role, profile, is_account_activated } = userInfo.user ?? {}
      if (role === 'super') navigate('/admin-dashboard')
      else if (role === 'partner') navigate('/partner-dashboard')
      else if (!is_account_activated) navigate('/home') // ProtectedRoutes will show payment
      else if (!profile?.onboarding_completed) {
        // Route to the right step based on how far they got
        if (profile?.welcome_video_watched) {
          navigate('/on-boarding/start') // Skip welcome page → go to videos (career picker auto-skips if careers exist)
        } else {
          navigate('/on-boarding')
        }
      }
      else navigate('/home')
    }
  }, [navigate, userInfo])

  const submitHandler = async (values: ILoginUser) => {
    try {
      const response = await login({
        email: values.email,
        password: values.password,
      }).unwrap()
      await dispatch(setCredentials({ ...response }))
      const { role, profile, is_account_activated } = response.user
      if (role === 'super') navigate('/admin-dashboard')
      else if (role === 'partner') navigate('/partner-dashboard')
      else if (!is_account_activated) navigate('/home')
      else if (!profile?.onboarding_completed) {
        if (profile?.welcome_video_watched) {
          navigate('/on-boarding/start')
        } else {
          navigate('/on-boarding')
        }
      }
      else navigate('/home')
    } catch (error) {
      const errorMessage = (error as any)?.data?.error || ''
      if (errorMessage.toLowerCase().includes('not verified')) {
        displayError(
          'Your account needs to be verified before logging in. Please check your email for the verification link.',
        )
        navigate('/register-success')
        return
      }
      displayError(
        errorMessage ||
          'Unable to sign in. Please try again or contact support.',
      )
    }
  }

  return (
    <div className="flex flex-col items-center justify-center bg-brand min-h-[calc(100vh-56px)] px-4 py-12">
      <div className="flex flex-col items-center gap-4 w-full max-w-[650px]">
        {/* Heading */}
        <header className="flex flex-col items-center gap-2 w-[381px] max-w-full">
          <h1 className="text-display-lg text-white text-center">
            Let’s start learning
          </h1>
          <p className="text-base font-normal leading-4 text-white text-center">
            Sign in or create an account
          </p>
        </header>

        {/* Card */}
        <div className="bg-white rounded-lg shadow-card w-full md:h-[372px] py-10 px-6">
          <div className="flex flex-col md:flex-row h-full">
            {/* Left — Login */}
            <div className="flex flex-col justify-center gap-4 flex-1">
              <img
                className="h-5 w-[182px] self-center"
                src="/assets/brand/CareerSight Logo Web-01.png"
                alt="CareerSight"
              />

              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={submitHandler}
              >
                {({ isSubmitting }) => (
                  <Form autoComplete="off" className="flex flex-col gap-4">
                    <FormikInput
                      type="text"
                      id="email"
                      label="Email"
                      name="email"
                      placeholder="Email Address"
                      maxLength={50}
                      sanitized
                    />
                    <FormikInput
                      type="password"
                      id="password"
                      label="Password"
                      name="password"
                      placeholder="Password"
                      isPassword
                      maxLength={50}
                      sanitized
                      showLink
                      linkText="Forgot password?"
                      onLinkClick={() => navigate('/forgot-password')}
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting || isLoading}
                      className="w-full"
                    >
                      {isLoading ? 'Loading...' : 'Sign In'}
                    </Button>
                  </Form>
                )}
              </Formik>
            </div>

            {/* Mobile divider */}
            <div className="flex items-center gap-3 my-6 md:hidden">
              <hr className="flex-1 border-border" />
              <span className="text-body-sm-emphasis text-text-muted">Or</span>
              <hr className="flex-1 border-border" />
            </div>

            {/* Vertical divider — desktop */}
            <div className="hidden md:block border-l border-[#E8E8E8] self-stretch mx-8" />

            {/* Right — Sign up options */}
            <div className="flex flex-col justify-between flex-1 md:h-[292px]">
              <p className="text-base font-semibold leading-4 text-headerBlue text-center">
                Sign up with a code
              </p>

              {/* Code Cards */}
              <div className="flex flex-col gap-2">
                {/* Redeem Code */}
                <button
                  type="button"
                  className="flex items-center gap-[10px] bg-surface-cream rounded-lg w-[269px] h-[76px] p-3 pr-0 overflow-hidden cursor-pointer hover:shadow-subtle transition-shadow border-none text-left"
                  onClick={() => navigate('/register?type=2')}
                >
                  <div className="flex flex-col justify-center flex-1">
                    <p className="text-body-emphasis text-text-heading">
                      Redeem code
                    </p>
                    <p className="text-body-sm text-text-muted">
                      Received a code? Click here to get started!
                    </p>
                  </div>
                  <div className="h-[76px] w-[76px] shrink-0">
                    <svg aria-hidden="true" width="76" height="76" viewBox="0 0 64 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="45" cy="36" r="45" fill="#213269" />
                      <circle cx="44.587" cy="35.587" r="36.33" fill="#5EA3E0" />
                      <line x1="26.883" y1="39.716" x2="35.505" y2="48.337" stroke="white" strokeWidth="17" strokeLinecap="round" />
                      <line x1="63.578" y1="24.489" x2="37.617" y2="50.45" stroke="white" strokeWidth="17" strokeLinecap="round" />
                    </svg>
                  </div>
                </button>

                {/* Purchase Code */}
                <button
                  type="button"
                  className="flex items-center gap-[10px] bg-surface-cream rounded-lg w-[269px] h-[76px] p-3 pr-0 overflow-hidden cursor-pointer hover:shadow-subtle transition-shadow border-none text-left"
                  onClick={() => navigate('/register?type=1')}
                >
                  <div className="flex flex-col justify-center flex-1">
                    <p className="text-body-emphasis text-text-heading">
                      Purchase code
                    </p>
                    <p className="text-body-sm text-text-muted">
                      Help someone discover their future.
                    </p>
                  </div>
                  <div className="h-[76px] w-[76px] shrink-0">
                    <svg aria-hidden="true" width="76" height="76" viewBox="0 0 65 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.705 80.638L41.372 87.15 41.047 32.822 0 25.184l4.705 55.454z" fill="#3A5A9E" />
                      <path d="M77.046 80.206 41.372 87.15l-.324-54.328L82 24.695l-4.954 55.511z" fill="#213269" />
                      <path d="M82 24.695 41.047 32.822 0 25.184l40.953-8.127L82 24.695z" fill="#3A5A9E" />
                      <path d="M14.945 82.457 13.405 27.609l39.908-8.776 12.483 2.486-40.982 8.137 2.149 55.193-12.018-2.192z" fill="#5EA3E0" />
                      <path d="M67.322 82.246l2.403-54.568-39.993-8.704-12.451 2.635 40.851 8.101-3.007 54.922 12.197-2.386z" fill="#5EA3E0" />
                      <path d="M69.469 2.562C49.162-9.595 39.606 25.462 39.606 25.462c46.121-8.021 29.863-22.9 29.863-22.9zM51.634 19.596s4.583-12.306 14.08-11.908c0 0 5.336 8.031-14.08 11.908z" fill="#5EA3E0" />
                      <path d="M12.215 2.904S-3.863 17.976 42.35 25.446c0 0-9.974-34.94-30.134-22.542zm5.57 1.584c13.376-1.99 16.099 11.612 16.099 11.612-13.663-3.43-16.1-12.612-16.1-12.612z" fill="#5EA3E0" />
                    </svg>
                  </div>
                </button>
              </div>

              {/* Create Account */}
              <div className="flex flex-col items-center gap-3">
                <p className="text-base font-semibold leading-4 text-headerBlue text-center">
                  Or create your own account
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/register')}
                  className="w-full"
                >
                  Create new account
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginScreen
